import { Suspense, lazy, useEffect, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import AppShell from '@/components/layout/AppShell';
import ProtectedRoute from '@/features/auth/components/ProtectedRoute';
import ErrorBoundary from '@/components/ErrorBoundary';
import UpdateBanner from '@/features/notificacoes/components/UpdateBanner';
import ReconnectingPill from '@/components/ReconnectingPill';
import NovoItemRequisicaoNotificacao from '@/features/suprimentos/components/NovoItemRequisicaoNotificacao';

import { AuthProvider } from '@/contexts/AuthContext';
import { ToastProvider } from '@/contexts/ToastContext';
import { SecurityAudit } from '@/features/admin/components/security/SecurityAudit';
import { queryClient } from '@/lib/queryClient';

import { NavigationProvider } from '@/app/navigation/NavigationProvider';
import { useNavigation } from '@/app/navigation/use-navigation';
import { notificationDrawerKind, type View } from '@/app/navigation/navigation-state';
import { Router } from '@/app/routing/Router';
import { OverlayDrawerHost } from '@/app/routing/OverlayDrawerHost';
import { getTenantSubdomain } from '@/utils/tenantDetection';
import FeedbackWidgetMount from '@/features/feedback/FeedbackWidgetMount';
import { FeedbackWidgetProvider } from '@/features/feedback/feedback-widget-context';
import PresencialKiosk from '@/features/proposta/components/signature/presencial/PresencialKiosk';
import {
  readHandoff,
  subscribeHandoff,
  type PresencialHandoff,
} from '@/features/proposta/components/signature/presencial/handoff';

// Lazy-loaded because OAuth is only hit when an external client (claude.ai,
// ChatGPT) redirects the user here for consent — keeps the main bundle slim.
const OAuthAuthorizePage = lazy(
  () => import('@/features/oauth/pages/OAuthAuthorizePage'),
);

// Lazy-loaded because the public signature page is only hit by a signatário
// following an invitation link — its pdfjs + package payload stays out of the
// main bundle.
const AssinaturaPublicaPage = lazy(
  () => import('@/pages/public/AssinaturaPublicaPage'),
);

// Lazy-loaded because the login e-mail change pages (JOSH-52) are only hit
// from the links mailed to the new address (confirm) and to the current one
// (cancel). Both are public: no login is required or used.
const ConfirmarEmailPage = lazy(
  () => import('@/pages/public/ConfirmarEmailPage'),
);
const CancelarTrocaEmailPage = lazy(
  () => import('@/pages/public/CancelarTrocaEmailPage'),
);

const OAUTH_AUTHORIZE_PATH = '/oauth/authorize';
const ASSINATURA_PUBLICA_PREFIX = '/p/aceite/';
const CONFIRMAR_EMAIL_PATH = '/conta/confirmar-email';
const CANCELAR_TROCA_EMAIL_PATH = '/conta/cancelar-troca-email';

// Infra subdomains that must always render the main app, never the public
// signature page. These are deployment hostnames (mirrored in vercel.json),
// not tenant-blocklist entries — a real invite link always uses a tenant
// subdomain, so anything else falling through here is anomalous by design.
const RESERVED_SUBDOMAINS = new Set(['app', 'www', 'api', 'mcp']);

function AppContent() {
  const { view, subview, actions } = useNavigation();

  return (
    <ProtectedRoute>
      {/* Bridges the headless feedback widget (mounted below) to the UserMenu's
          "Reportar problema" / "Meus tickets" entries + resolve badge — they sit
          in sibling subtrees (AppShell vs FeedbackWidgetMount). Must wrap both. */}
      <FeedbackWidgetProvider>
      <div>
        {/* Bottom-centre notices (JOSH-89): the reconnecting pill stacks above
            the update banner, never overlapping it. */}
        <div className="bottom-notice-stack">
          <ReconnectingPill />
          <UpdateBanner />
        </div>

        <NovoItemRequisicaoNotificacao
          onIrParaSeparacao={(requisicaoId: string) => {
            actions.setRequisicaoNotification(requisicaoId, true);
            actions.navigate('suprimentos-requisicoes-ativas');
          }}
        />

        <AppShell
          currentView={view}
          currentSubview={subview}
          onNavigate={(targetView: string, subviewId?: string, params?: { id?: string }) =>
            actions.navigate(targetView as View, {
              subview: subviewId ?? null,
              viewParams: params?.id ? { initialOpenRecordId: params.id } : null,
            })
          }
          onOpenDrawer={(kind: string, id: string) =>
            actions.pushOverlayDrawer(notificationDrawerKind(kind), id)
          }
        >
          <Router />
        </AppShell>

        <OverlayDrawerHost />
        <FeedbackWidgetMount />
      </div>
      </FeedbackWidgetProvider>
    </ProtectedRoute>
  );
}

export default function App() {
  const [handoff, setHandoff] = useState<PresencialHandoff | null>(() => readHandoff());
  useEffect(() => subscribeHandoff(setHandoff), []);

  const pathname = window.location.pathname;

  // Path-based exception for the OAuth consent flow. claude.ai / ChatGPT
  // redirect users to /oauth/authorize, which renders its own AuthShell and
  // bypasses ProtectedRoute, NavigationProvider, and AppShell entirely.
  const isOAuthAuthorize = pathname === OAUTH_AUTHORIZE_PATH;

  // Path-based exception for the public signature flow. A signatário follows
  // `<tenant>.appjoshua.com/p/aceite/<token>`; like the OAuth carve-out the
  // page renders outside ProtectedRoute / NavigationProvider / AppShell.
  // Reserved infra subdomains fall through to the main app.
  const isAssinaturaPublica =
    pathname.startsWith(ASSINATURA_PUBLICA_PREFIX) &&
    !RESERVED_SUBDOMAINS.has(getTenantSubdomain() ?? '');

  // Path-based exceptions for the login e-mail change links (JOSH-52). The
  // parameters travel in the hash, so only the path is matched here.
  const normalizedPath = pathname.replace(/\/+$/, '');
  const isConfirmarEmail = normalizedPath === CONFIRMAR_EMAIL_PATH;
  const isCancelarTrocaEmail = normalizedPath === CANCELAR_TROCA_EMAIL_PATH;

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            {handoff ? (
              <PresencialKiosk record={handoff} />
            ) : isOAuthAuthorize ? (
              <Suspense fallback={null}>
                <OAuthAuthorizePage />
              </Suspense>
            ) : isAssinaturaPublica ? (
              <Suspense fallback={null}>
                <AssinaturaPublicaPage />
              </Suspense>
            ) : isConfirmarEmail ? (
              <Suspense fallback={null}>
                <ConfirmarEmailPage />
              </Suspense>
            ) : isCancelarTrocaEmail ? (
              <Suspense fallback={null}>
                <CancelarTrocaEmailPage />
              </Suspense>
            ) : (
              <SecurityAudit>
                <NavigationProvider>
                  <AppContent />
                </NavigationProvider>
              </SecurityAudit>
            )}
          </AuthProvider>
        </ToastProvider>
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
